import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { countries } from '../../data/countries';
import { useTranslation } from 'react-i18next';

interface SignupProps {
  userType: 'player' | 'admin';
}

const Signup: React.FC<SignupProps> = ({ userType }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    city: '',
    // Player optional fields
    gender: '',
    dob: '',
    // Admin specific fields
    businessType: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [similarPlayers, setSimilarPlayers] = useState<Array<{ id: number; name: string; email: string | null }>>([]);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Performs the actual signup call — used both directly and from the similar-names modal.
  const doSignup = async () => {
    setIsLoading(true);
    setError('');
    try {
      await signup(formData, userType);
      if (userType === 'admin') navigate('/admin/dashboard');
      else navigate('/player/dashboard');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('auth.signupFailed');
      const lower = msg.toLowerCase();
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('already') || lower.includes('duplicate'))) {
        setError('This email already has an account associated with it. Try logging in or resetting your password.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
      setShowSimilarModal(false);
      setSimilarPlayers([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('validation.passwordMinLength'));
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError(t('validation.nameRequired'));
      return;
    }

    if (!formData.country || !formData.city) {
      setError(t('validation.locationRequired'));
      return;
    }

    // Player-only: check for similar names before creating account
    if (!isAdmin) {
      try {
        const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
        const res = await fetch('/api/auth/similar-players', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: fullName }),
        });
        const data = await res.json();
        if (data.success && data.players && data.players.length > 0) {
          setSimilarPlayers(data.players);
          setShowSimilarModal(true);
          return;
        }
      } catch {
        // Non-blocking — proceed if check fails
      }
    }

    await doSignup();
  };

  const isAdmin = userType === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      {/* Similar Players Modal — players only */}
      {showSimilarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-yellow-400/40 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-5">
              <div className="text-5xl mb-3">🔍</div>
              <h3 className="text-xl font-bold text-white">Similar Accounts Found</h3>
              <p className="text-yellow-300 mt-2 text-sm">We found players with similar names already in the system. If any of these is you, cancel and log in instead.</p>
            </div>
            <div className="space-y-2 mb-5 max-h-52 overflow-y-auto">
              {similarPlayers.map(p => (
                <div key={p.id} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-semibold">{p.name}</p>
                    <p className="text-blue-300 text-xs">{p.email || 'No email'}</p>
                  </div>
                  <span className="text-gray-400 text-xs">ID #{p.id + 100}</span>
                </div>
              ))}
            </div>
            <p className="text-orange-300 text-xs text-center mb-5">If any of the above is you, click Cancel and log in instead.</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowSimilarModal(false); setSimilarPlayers([]); }}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2.5 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doSignup}
                disabled={isLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
              >
                {isLoading ? 'Creating...' : 'No, Sign me up!'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-white mb-2">
              🎳 {t('common.appName')}
            </h1>
          </Link>
          <h2 className={`text-xl font-semibold ${isAdmin ? 'text-purple-400' : 'text-blue-400'}`}>
            {isAdmin ? t('auth.adminRegistration') : t('auth.playerRegistration')}
          </h2>
        </div>

        {/* Signup Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.firstName')}
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('auth.firstNamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.lastName')}
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('auth.lastNamePlaceholder')}
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.emailAddress')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.enterEmail')}
              />
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.phoneNumber')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.phonePlaceholder')}
              />
            </div>

            {/* Country Field */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.country')} *
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [&>option]:bg-gray-800 [&>option]:text-white"
              >
                <option value="" className="bg-gray-800 text-white">{t('auth.selectCountry')}</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name} className="bg-gray-800 text-white">
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* City Field */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.city')} *
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.enterCity')}
              />
            </div>

            {/* Player Optional Fields: Gender & DOB */}
            {!isAdmin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-2">
                    Gender <span className="text-gray-500 font-normal text-xs">(Optional)</span>
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [&>option]:bg-gray-800 [&>option]:text-white"
                  >
                    <option value="" className="bg-gray-800 text-white">Prefer not to say</option>
                    <option value="M" className="bg-gray-800 text-white">Male</option>
                    <option value="F" className="bg-gray-800 text-white">Female</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="dob" className="block text-sm font-medium text-gray-300 mb-2">
                    Date of Birth <span className="text-gray-500 font-normal text-xs">(Optional)</span>
                  </label>
                  <input
                    id="dob"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [color-scheme:dark]"
                  />
                </div>
              </div>
            )}

            {/* Admin Specific Fields */}
            {isAdmin && (
              <>
                
                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.businessType')}
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent [&>option]:bg-gray-800 [&>option]:text-white"
                  >
                    <option value="" className="bg-gray-800 text-white">{t('auth.selectBusinessType')}</option>
                    <option value="bowling_center" className="bg-gray-800 text-white">{t('auth.bowlingCenter')}</option>
                    <option value="league_organizer" className="bg-gray-800 text-white">{t('auth.leagueOrganizer')}</option>
                    <option value="tournament_director" className="bg-gray-800 text-white">{t('auth.tournamentDirector')}</option>
                    <option value="other" className="bg-gray-800 text-white">{t('auth.other')}</option>
                  </select>
                </div>
              </>
            )}

            {/* Password Fields */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.passwordPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.confirmPasswordPlaceholder')}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition duration-200 ${
                isAdmin
                  ? 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            <Link
              to={`/login/${userType}`}
              className={`block ${isAdmin ? 'text-purple-400 hover:text-purple-300' : 'text-blue-400 hover:text-blue-300'} text-sm transition duration-200`}
            >
              {t('auth.haveAccount')}
            </Link>
            
            <Link
              to="/"
              className="block text-gray-400 hover:text-white text-sm transition duration-200"
            >
              ← {t('common.backToHome')}
            </Link>
            
            {!isAdmin && (
              <Link
                to="/signup/admin"
                className="block text-gray-400 hover:text-white text-sm transition duration-200"
              >
                {t('auth.areYouAdmin')}
              </Link>
            )}
            
            {isAdmin && (
              <Link
                to="/signup/player"
                className="block text-gray-400 hover:text-white text-sm transition duration-200"
              >
                {t('auth.areYouPlayer')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
